import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { useAuth } from "../utils/AuthContext.tsx";
import { useEstimate, useFile, useDeleteFile } from "@/utils/Api.ts";
import { ContactInfoModel, EstimateItem } from "@/utils/Types.ts";
import { DataTable } from "./DataTable.tsx";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "./ui/button.tsx";
import { BanIcon, DownloadIcon, ExternalLink, SparkleIcon, TrashIcon } from "lucide-react";
import { Skeleton } from "./ui/skeleton.tsx";
import { useEffect, useState } from "react";

const ContactInfo: React.FC<{ contact: ContactInfoModel }> = ({ contact }) => {
  return (
    <div className="py-2 px-2">
      <div className="font-bold">{contact.name ? contact.name : "Unknown"}</div>
      <div className="font-mono text-sm">
        {contact.streetAddress && (
          <>
            {contact.streetAddress.houseNumber && <div>{contact.streetAddress.houseNumber}</div>}
            <div>
              {contact.streetAddress.city}
              {contact.streetAddress.state ? `, ${contact.streetAddress.state} ${contact.streetAddress.zip4}` : ""}
            </div>
          </>
        )}

        {contact.emailAddress && <div>{contact.emailAddress}</div>}
        {contact.phoneNumber && <div>{contact.phoneNumber} </div>}
      </div>
    </div>
  );
};

const EstimateView: React.FC<{ fileId: string; onClose: () => void }> = ({ fileId, onClose }) => {
  const { token, currentAccount } = useAuth();
  const isMobile = useIsMobile();
  const [currentFile, setCurrentFile] = useState<string | undefined>(undefined);

  const { data: fileData, isFetching: isFetchingFile } = useFile(currentFile, token ?? undefined);
  const { data: estimate, isFetching: isFetchingEstimate } = useEstimate(currentFile, token ?? undefined);

  useEffect(() => {
    setCurrentFile(fileId);
  }, [fileId]);

  const handleClose = () => {
    setCurrentFile(undefined);
    if (onClose) onClose();
  };

  const { mutate: removeFile } = useDeleteFile(currentAccount?.id, token ?? undefined);
  const onDeleteFile = (id: string) => {
    removeFile({ fileId: id });
    handleClose();
  };

  const columns: ColumnDef<EstimateItem>[] = [
    {
      accessorKey: "csiCode",
      header: "Code",
      cell: ({ row }) => {
        return <div className="font-mono text-xs font-bold text-nowrap">{row.getValue("csiCode")}</div>;
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => {
        return <div className="font-mono text-xs">{row.getValue("description")}</div>;
      },
    },
    {
      accessorKey: "quantity",
      header: "Qty",
      cell: ({ row }) => {
        return <div className="font-mono text-xs">{(row.getValue("quantity") as number).toFixed(2)}</div>;
      },
    },
    {
      accessorKey: "unitPrice",
      header: "Unit Price",
      cell: ({ row }) => {
        return (
          <div className="font-mono text-xs text-nowrap text-right pr-3">
            ${(row.getValue("unitPrice") as number).toFixed(2)} USD
          </div>
        );
      },
    },
  ];

  return (
    <>
      {
        <Sheet open={currentFile != undefined} modal={true} onOpenChange={() => handleClose()}>
          <SheetContent
            className="py-2 h-[80vh] shadow-none overflow-y-scroll overflow-x-clip md:h-[100vh] lg:inline lg:px-8 lg:[&>button]:hidden lg:max-w-[640px] xl:pl-8"
            side={isMobile ? "bottom" : "right"}
            onInteractOutside={() => handleClose()}
          >
            <SheetHeader>
              <SheetTitle className="px-2 py-4 pt-6">
                <div className="flex h-12 gap-1">
                  <Button variant="default">
                    <DownloadIcon strokeWidth={3} />
                    Original
                  </Button>
                  <Button size="icon" variant="ghost">
                    <BanIcon strokeWidth={3} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => onDeleteFile(currentFile!)}>
                    <TrashIcon strokeWidth={3} />
                  </Button>
                </div>
                <div className="flex-grow scroll-m-20 text-2xl font-semibold tracking-tight text-left text-accent-foreground py-4">
                  {isFetchingFile && "..."}
                  {!isFetchingFile && fileData?.filename}
                </div>
                {estimate && <div className="text-base font-normal text-left py-4">{estimate.summary}</div>}
              </SheetTitle>
            </SheetHeader>

            {isFetchingEstimate && (
              <div className="flex flex-col gap-4 p-2">
                <div className="flex items-center gap-1">
                  <SparkleIcon />
                  Processing...
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            )}

            {estimate && (
              <div className="border-2 rounded-md">
                <div className="relative text-base font-normal py-8 px-2 bg-muted">
                  <div className="flex-grow font-semibold text-xl px-2">Estimate</div>
                  {estimate && estimate.vendor && <ContactInfo contact={estimate.vendor} />}
                  <span className="absolute top-2 right-2 p-2 font-mono font-semibold text-xs">
                    {estimate.id ? `ID #${estimate.id}, ` : ""} Created {estimate.createDate}
                  </span>
                </div>
                <div className="px-2">
                  <div>
                    <DataTable columns={columns} data={estimate.lineItems} />
                  </div>
                  <div className="font-mono font-semibold text-base py-4 pr-4 text-right">
                    ${estimate.totalPrice.toFixed(2)} USD
                  </div>

                  {estimate.qualifications.length > 0 && (
                    <div className="px-2">
                      <div className="text-base font-semibold pb-2">Notes</div>
                      <ul className="font-mono text-sm list-disc list-outside pl-4">
                        {estimate.qualifications.map((q, i) => (
                          <li key={i} className="pl-2">
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {estimate.openItems.length > 0 && (
                    <div className="px-2">
                      <div className="text-base font-semibold pt-4 pb-2"> Open Items</div>
                      <ul className="font-mono text-sm list-disc list-inside">
                        {estimate.openItems.map((q, i) => (
                          <li key={i} className="pl-2">
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex gap-8 pt-8 pb-8">
                    {estimate.recipient && (
                      <div>
                        <div className="font-normal text-xs uppercase px-2">Recipient</div>
                        <ContactInfo contact={estimate.recipient} />
                      </div>
                    )}
                    {estimate.billable && (
                      <div>
                        <div className="font-normal text-xs uppercase px-2">Billing Contact</div>
                        <ContactInfo contact={estimate.billable} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {estimate && (
              <div className="flex justify-end mb-8">
                <Button variant="link">
                  View Original
                  <ExternalLink />
                </Button>
              </div>
            )}
          </SheetContent>
        </Sheet>
      }
    </>
  );
};

export { EstimateView };
